import { Injectable, ErrorHandler } from '@angular/core';
import { Router } from '@angular/router';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';

@Injectable({
  providedIn: 'root'
})
export class ApplicationInsightsService {
  private appInsights: ApplicationInsights;
  private angularPlugin: AngularPlugin;
  private isInitialized = false;

  constructor(private router: Router) {
    // Application Insights will be initialized in init() method after we have the instrumentation key
  }

  /**
   * Initialize Application Insights with instrumentation key
   * @param instrumentationKey - Application Insights instrumentation key from Azure
   */
  init(instrumentationKey: string): void {
    if (!instrumentationKey) {
      console.warn('Application Insights instrumentation key not provided. Browser telemetry will not be collected.');
      return;
    }

    if (this.isInitialized) {
      console.warn('Application Insights already initialized.');
      return;
    }

    try {
      // Initialize Angular plugin
      this.angularPlugin = new AngularPlugin();

      // Initialize Application Insights with instrumentation key
      this.appInsights = new ApplicationInsights({
        config: {
          instrumentationKey: instrumentationKey,
          enableAutoRouteTracking: true, // Automatically track route changes
          enableCorsCorrelation: false, // Disabled to avoid CORS issues with third-party services
          enableRequestHeaderTracking: false, // Disabled along with CORS correlation
          enableResponseHeaderTracking: false, // Disabled along with CORS correlation
          disableFetchTracking: false,
          extensions: [this.angularPlugin],
          extensionConfig: {
            [this.angularPlugin.identifier]: {
              router: this.router,
              errorServices: [new ErrorHandler()] // Chain with existing error handlers
            }
          }
        }
      });

      this.appInsights.loadAppInsights();
      this.appInsights.trackPageView(); // Initial page view
      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize Application Insights:', error);
    }
  }

  /**
   * Track a page view
   */
  logPageView(name?: string, uri?: string): void {
    if (!this.isInitialized) return;
    this.appInsights.trackPageView({ name, uri });
  }

  /**
   * Track a custom event
   */
  logEvent(name: string, properties?: { [key: string]: any }): void {
    if (!this.isInitialized) return;
    this.appInsights.trackEvent({ name }, properties);
  }

  /**
   * Track a custom metric
   */
  logMetric(name: string, average: number, properties?: { [key: string]: any }): void {
    if (!this.isInitialized) return;
    this.appInsights.trackMetric({ name, average }, properties);
  }

  /**
   * Track an exception
   */
  logException(exception: Error, severityLevel?: number): void {
    if (!this.isInitialized) return;
    this.appInsights.trackException({ exception, severityLevel });
  }

  /**
   * Track a trace message
   */
  logTrace(message: string, properties?: { [key: string]: any }): void {
    if (!this.isInitialized) return;
    this.appInsights.trackTrace({ message }, properties);
  }

  /**
   * Set authenticated user context
   */
  setAuthenticatedUserContext(userId: string, accountId?: string): void {
    if (!this.isInitialized) return;
    this.appInsights.setAuthenticatedUserContext(userId, accountId);
  }

  /**
   * Clear authenticated user context
   */
  clearAuthenticatedUserContext(): void {
    if (!this.isInitialized) return;
    this.appInsights.clearAuthenticatedUserContext();
  }

  /**
   * Check if Application Insights is initialized
   */
  get initialized(): boolean {
    return this.isInitialized;
  }
}
